import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, ERROR, errorTypes, SHORT_FULL_DATE_FORMAT } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import notificationsService from '@/services/notificationsService';
import titleService from '@/services/titleService';
import { booleanCellRenderer, LangTextMandatory } from '@/types';
import styles from './Emails.module.scss';

const COLUMNS_NAMES = {
  TITLE: 'title',
  MODIFICATION_DATE: 'modificationDate',
  MODIFICATION_USER_CODE: 'modificationUserCode',
  CREATION_DATE: 'creationDate',
  CREATION_USER_CODE: 'creationUserCode',
  USED: 'used',
};

export interface EmailTemplate {
  used: boolean;
  creationDate?: string;
  modificationDate?: string;
  creationUserCode?: string;
  id?: string;
  title?: LangTextMandatory;
}

interface EmailTemplatesProps extends EmailTemplate {
  timeZone: string;
}

const EmailTemplates: React.FC<EmailTemplatesProps> = (props) => {
  const { timeZone } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { addToast } = useToastContext();
  const { goTo } = useGoTo();
  const [popupOpen, setPopupOpen] = useState<string>('');
  const [selection, setSelection] = useState<string[]>([]);
  const [emails, setEmails] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.templatesSection'));
  }, [t, lang]);

  const getEmails = () => {
    setLoading(true);
    notificationsService.getEmailTemplates()
      .then((data) => {
        setEmails(data);
        setSelection([]);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  const columns = useMemo(() => [
    { name: COLUMNS_NAMES.TITLE, title: t('editableFields.title'), flex: 2 },
    { name: COLUMNS_NAMES.MODIFICATION_DATE, title: t('administration.emails.modificationDate'), flex: 2 },
    { name: COLUMNS_NAMES.MODIFICATION_USER_CODE, title: t('administration.emails.modificationUserCode'), flex: 2 },
    { name: COLUMNS_NAMES.CREATION_DATE, title: t('editableFields.timestampCreating'), flex: 2 },
    { name: COLUMNS_NAMES.CREATION_USER_CODE, title: t('editableFields.creator'), flex: 1 },
    { name: COLUMNS_NAMES.USED, title: t('editableFields.inUse'), flex: 1, cellRenderer: booleanCellRenderer },
  ], [t]);

  const emailTemplateData = useMemo(() => emails.map((result) => ({
    ...result,
    title: result?.title?.[lang],
    id: result.id,
    used: result.used,
    creationDate: getFullFormattedDate(result.creationDate, timeZone, SHORT_FULL_DATE_FORMAT),
    modificationDate: getFullFormattedDate(result.modificationDate, timeZone, SHORT_FULL_DATE_FORMAT),
  })), [emails, lang, timeZone]);

  const handleDeleteSecteds = () => {
    setLoading(true);
    setPopupOpen('');
    notificationsService.removeEmail(selection)
      .then(() => {
        addToast(t('dialog.deleteEmailTemplate.success'), 'Success');
        getEmails();
      })
      .catch((error) => addToast(
        error.code === errorTypes.EMAIL_TEMPLATE_ACTIVE
          ? t('dialog.deleteEmailTemplate.stillInUseError')
          : error.message,
        ERROR,
      ))
      .finally(() => setLoading(false));
  };

  const createCopyOfSelected = () => {
    setLoading(true);
    const selectedEmailId = selection[0];
    notificationsService.createCopyEmailTemplate(selectedEmailId)
      .then(() => {
        addToast(t('administration.emails.templateCopied'), 'Success');
        getEmails();
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getEmails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <AccordionPanel title={`${t('menu.emails')} ${emails ? `(${emails.length})` : ''}`}>
        <div className={styles.ButtonsTable}>
          <div className={styles.ButtonsContentTableRight}>
            <Button
              id="upload-xls-file"
              buttonStyle="TableButton"
              btnText={t('administration.emails.addNewTemplate')}
              handleClick={() => goTo('/administration/emails/templates/new-email')}
            />
            <IcoButtonSimple
              disabled={selection.length !== 1 || loading}
              id="btn-edit-campaign"
              icon="icon-stift-bearbeiten"
              tooltipText={t('campaign.editTitle')}
              handleClick={() => goTo(`/administration/emails/templates/${selection[0]}`)}
            />
            <IcoButtonSimple
              disabled={selection.length !== 1 || loading}
              id="btn-copy-workflow"
              icon="icon-datei-doppelt-kopieren"
              tooltipText={t('copy')}
              handleClick={createCopyOfSelected}
            />
            <IcoButtonSimple
              disabled={!(selection.length > 0) || loading}
              id="btn-delete-plan"
              icon="icon-muelleimer-loeschen"
              tooltipText={t('dialog.delete')}
              handleClick={() => setPopupOpen('DELETED')}
            />
          </div>
        </div>
        <TableListEditableNew
          hasCheckbox
          columnId="id"
          list={emailTemplateData}
          columns={columns}
          selection={selection}
          onSelectionChange={(newSelection) => setSelection(newSelection as string[])}
          loading={loading}
        />
      </AccordionPanel>

      {popupOpen === 'DELETED' && (
        <ConfirmDialog
          reject={() => setPopupOpen('')}
          confirm={handleDeleteSecteds}
          title={`${t('dialog.delete')} ${t('dialog.deleteEmailTemplate.title')}`}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.remove')}
        >
          <p className={styles.RemovePlantsMessage}>
            {`${t(`dialog.deleteEmailTemplate.message`)}`}
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  timeZone: getSettingsInfo(state)?.timezone,
});

export default connect(mapStateToProps)(EmailTemplates);
